import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Form,
    Input,
    Modal,
    Popconfirm,
    Space,
    Table,
    Typography,
} from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { ClientMetadataRow } from "../../../model/client/ClientMetadataRow";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text, Paragraph } = Typography;

export const MAX_NAME_LENGTH = 100;
export const MAX_VALUE_LENGTH = 10 * 1024;

// Mirrors the backend ClientMetadataService.ReservedPrefixes: names under a
// client_setting section prefix would masquerade as typed settings.
export const RESERVED_PREFIXES = [
    "branding.",
    "contact.",
    "locale.",
    "tax.",
    "hours.",
    "fees.",
    "forms.",
    "banners.",
    "checkin.",
    "billing.",
];

export function reservedPrefixFor(name: string): string | null {
    const lower = name.toLowerCase();
    return RESERVED_PREFIXES.find((p) => lower.startsWith(p)) ?? null;
}

interface Draft {
    // null = creating a new entry; otherwise the original name being edited.
    originalName: string | null;
    name: string;
    value: string;
}

// Settings panel for arbitrary tenant-scoped key/value metadata (FORT-134).
// Mirrors PosItemTypesPanel: ignores the controlled value/onChange props
// because this section owns a list of rows, not a single value. Add/edit go
// through the bulk-replace PUT (the authoritative full set); delete uses the
// single-key DELETE. Every change is audit-tracked server-side.
export const ClientMetadataPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;

    const [rows, setRows] = useState<ClientMetadataRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const [draft, setDraft] = useState<Draft | null>(null);
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!Number.isFinite(clientId)) return;
        try {
            const next = await service.listClientMetadata(clientId);
            setRows(next);
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load metadata.');
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError('Invalid client id in URL.');
            return;
        }
        setLoadError(null);
        void refresh();
    }, [clientId, refresh]);

    const handleOpenCreate = () => {
        setDraft({ originalName: null, name: '', value: '' });
        setActionError(null);
    };

    const handleOpenEdit = (row: ClientMetadataRow) => {
        setDraft({ originalName: row.name, name: row.name, value: row.value });
        setActionError(null);
    };

    const handleCloseModal = () => {
        if (saving) return;
        setDraft(null);
        setActionError(null);
    };

    const trimmedName = draft?.name.trim() ?? '';
    const reserved = trimmedName.length > 0 ? reservedPrefixFor(trimmedName) : null;
    const nameTakenByOther =
        draft !== null &&
        trimmedName.length > 0 &&
        (rows ?? []).some((r) => r.name === trimmedName && r.name !== draft.originalName);
    const valueTooLong = (draft?.value.trim().length ?? 0) > MAX_VALUE_LENGTH;
    const nameError =
        trimmedName.length === 0
            ? 'Name is required.'
            : trimmedName.length > MAX_NAME_LENGTH
                ? `Names are capped at ${MAX_NAME_LENGTH} characters.`
                : trimmedName.includes('/')
                ? "Names must not contain '/'."
                : reserved !== null
                    ? `Names starting with '${reserved}' are reserved for client settings.`
                    : nameTakenByOther
                        ? 'A metadata entry with this name already exists.'
                        : null;
    const valueError = valueTooLong
        ? `Values are capped at ${MAX_VALUE_LENGTH} characters.`
        : null;
    const draftValid = draft !== null && nameError === null && valueError === null;

    const handleSave = async () => {
        if (draft === null || !draftValid || rows === null) return;
        setSaving(true);
        setActionError(null);
        try {
            // Build the authoritative full set: current rows minus the entry
            // being edited (matched by its original name), plus the draft.
            const entries = rows
                .filter((r) => r.name !== draft.originalName)
                .map((r) => ({ name: r.name, value: r.value }));
            entries.push({ name: trimmedName, value: draft.value.trim() });
            await service.replaceClientMetadata(clientId, { entries });
            setDraft(null);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save metadata.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = useCallback(async (row: ClientMetadataRow) => {
        try {
            await service.deleteClientMetadata(clientId, row.name);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to delete metadata entry.');
        }
    }, [service, clientId, refresh]);

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name' as const,
            width: '35%',
            render: (name: string, row: ClientMetadataRow) => (
                <Text code data-testid={`cmd-row-name-${row.id}`}>{name}</Text>
            ),
        },
        {
            title: 'Value',
            dataIndex: 'value' as const,
            render: (value: string, row: ClientMetadataRow) => (
                <span data-testid={`cmd-row-value-${row.id}`}>{value}</span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 170,
            render: (_: unknown, row: ClientMetadataRow) => (
                <Space>
                    <Button
                        size="small"
                        data-testid={`cmd-edit-${row.id}`}
                        onClick={() => handleOpenEdit(row)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this metadata entry?"
                        description="The change is recorded in the audit log."
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger data-testid={`cmd-delete-${row.id}`}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ], [handleDelete]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="cmd-load-error" />;
    }
    if (rows === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Free-form key/value data for this club that doesn&apos;t fit a typed settings
                section. Every change is audit-tracked.
            </Paragraph>
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button
                    type="primary"
                    data-testid="cmd-add-btn"
                    onClick={handleOpenCreate}
                >
                    Add metadata entry
                </Button>
            </Space>
            {actionError && (
                <Alert type="error" message={actionError} data-testid="cmd-action-error" />
            )}
            <Table
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={false}
                data-testid="cmd-table"
                locale={{ emptyText: 'No metadata entries yet.' }}
            />
            <Modal
                open={draft !== null}
                title={draft?.originalName === null ? 'Add metadata entry' : 'Edit metadata entry'}
                onCancel={handleCloseModal}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                okButtonProps={{
                    disabled: !draftValid,
                    'data-testid': 'cmd-modal-save',
                } as any}
                cancelButtonProps={{ disabled: saving }}
                destroyOnClose
            >
                <Form layout="vertical" data-testid="cmd-form">
                    <Form.Item
                        label="Name"
                        validateStatus={draft !== null && nameError !== null ? 'error' : undefined}
                        help={draft !== null ? nameError ?? undefined : undefined}
                    >
                        <Input
                            data-testid="cmd-form-name"
                            value={draft?.name ?? ''}
                            onChange={(e) => setDraft((d) => d && { ...d, name: e.target.value })}
                            autoFocus
                            maxLength={MAX_NAME_LENGTH}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Value"
                        validateStatus={valueError !== null ? 'error' : undefined}
                        help={valueError ?? undefined}
                    >
                        <Input.TextArea
                            data-testid="cmd-form-value"
                            value={draft?.value ?? ''}
                            onChange={(e) => setDraft((d) => d && { ...d, value: e.target.value })}
                            rows={4}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};
